import type { RouterConfig } from '@nuxt/schema'
import type { RouteLocationNormalized } from 'vue-router'
import { START_LOCATION } from 'vue-router'
import { nextTick } from 'vue'
import { useNuxtApp } from '#app'

// Resolves once the hash target can actually exist in the DOM.
//
// Two things make "measure it now" wrong. layouts/default.vue leaves
// `.pad-top` off the landing page, so leaving it adds $nav-height of padding
// to <main> after the router has measured — a nav-height short. And a page
// whose setup suspends (pages/index.vue's weather widget awaits useFetch) is
// not mounted at all after nextTick on a client-side navigation; vue-router
// then finds no element and silently skips the scroll. Nuxt's own default
// scrollBehaviour waits for `page:loading:end` for exactly this reason, so on
// a page change this does the same. Same-page hashes and the initial load
// (where that hook has already fired) only need the DOM to settle.
async function targetIsMounted(to: RouteLocationNormalized, from: RouteLocationNormalized) {
    const changingPage = from !== START_LOCATION
        && to.path.replace(/\/$/, '') !== from.path.replace(/\/$/, '')

    if (changingPage) {
        const nuxtApp = useNuxtApp()
        await new Promise<void>(resolve => nuxtApp.hooks.hookOnce('page:loading:end', () => resolve()))
    } else {
        await nextTick()
    }

    await new Promise(resolve => requestAnimationFrame(resolve))
}

function baseRouteName(name: string | symbol | null | undefined): string {
    return String(name ?? '').replace(/___[a-z]+$/, '')
}

// https://router.vuejs.org/api/interfaces/routeroptions.html
export default <RouterConfig>{
    async scrollBehavior(to, from, savedPosition) {
        if (to.hash) {
            await targetIsMounted(to, from)

            return {
                el: to.hash,
                // Offset so the target isn't hidden under the fixed nav
                // (floating desktop bar ends ~130px down / 70px mobile header)
                top: typeof window !== 'undefined' && window.innerWidth < 900 ? 85 : 145,
                behavior: 'smooth'
            }
        }

        if (savedPosition) {
            return savedPosition
        }

        const isLocaleSwitch = from !== START_LOCATION
            && baseRouteName(to.name) === baseRouteName(from.name)

        if (isLocaleSwitch) {
            return false
        }

        return {
            top: 0,
            behavior: 'smooth'
        }
    }
}
